import { getErrorMessage } from "@utils/getErrorMessage";
import { createSignal } from "solid-js";
import {
	confirmAskPagePurchase,
	purchaseAskPagePass,
	subscribeToAskPage,
} from "@/client";
import {
	completeStripePaymentAction,
	type StripePaymentActionChallenge,
	stripePaymentActionChallenge,
} from "~/modules/shared/billing/stripePaymentAction";
import type { AskPagePurchaseOption } from "./AskPageAccessNotice";

/**
 * `charging` covers the request itself; `authenticating` is the 3-D Secure
 * challenge, which is worth naming separately because the visitor is looking
 * at their bank's dialog rather than at this page and the wait is theirs to
 * end.
 */
export type AskPagePurchaseState = "idle" | "charging" | "authenticating";

type ErrorBody = {
	error?: string;
	status?: string;
	client_secret?: string;
	publishable_key?: string;
	payment_intent_id?: string;
};

/**
 * Owns buying an AskPage pass or subscription: the request, the 3-D Secure
 * round trip, and turning any failure into one sentence the visitor can act
 * on. Structured identically to useTopupPurchase - the two purchase
 * endpoints (pass vs subscription) differ, but the challenge/confirm seam is
 * the same shared helper both call.
 *
 * The server is the only thing that decides a purchase succeeded. After a
 * challenge this calls the confirm endpoint rather than trusting Stripe.js's
 * word in the browser.
 */
export const useAskPagePurchase = () => {
	const [state, setState] = createSignal<AskPagePurchaseState>("idle");
	const [error, setError] = createSignal<string | null>(null);

	const busy = () => state() !== "idle";

	const authenticate = async (
		challenge: StripePaymentActionChallenge,
	): Promise<boolean> => {
		setState("authenticating");

		const result = await completeStripePaymentAction({
			challenge,
			confirm: (paymentIntentId) =>
				confirmAskPagePurchase({
					body: { payment_intent_id: paymentIntentId },
				}),
			confirmFailureMessage:
				"Your payment went through but we couldn't confirm it. Please check back in a moment before trying again.",
		});

		if (result.kind === "success") {
			return true;
		}

		setError(result.error);
		return false;
	};

	/**
	 * Buy `option` for `handle` with the stored card `paymentMethodId`.
	 * Resolves true once the entitlement is granted (immediately, or after a
	 * completed 3-D Secure challenge), or false after setting an error the
	 * caller can display.
	 */
	const purchase = async (args: {
		option: AskPagePurchaseOption;
		handle: string;
		paymentMethodId: string;
	}): Promise<boolean> => {
		setError(null);
		setState("charging");

		try {
			const res =
				args.option.type === "subscription"
					? await subscribeToAskPage({
							body: {
								handle: args.handle,
								payment_method_id: args.paymentMethodId,
							},
						})
					: await purchaseAskPagePass({
							body: {
								handle: args.handle,
								type: args.option.type,
								payment_method_id: args.paymentMethodId,
							},
						});

			if (res.data) {
				return true;
			}

			const body = res.error as ErrorBody | undefined;

			const challenge = stripePaymentActionChallenge(body);

			// A 402 is either a decline or a challenge; only the challenge carries
			// a client secret, and only it is worth continuing with.
			if (challenge) {
				return await authenticate(challenge);
			}

			setError(getErrorMessage(body?.error, "Your payment couldn't be taken."));

			return false;
		} catch (thrown) {
			setError(getErrorMessage(thrown, "Your payment couldn't be taken."));
			return false;
		} finally {
			setState("idle");
		}
	};

	return { purchase, state, busy, error, setError };
};
